import { Children, ComponentType, Fragment, ReactNode, isValidElement, useCallback, useMemo } from "react";

import { Spacing, Stack } from "capitalrx-components-2.0/lib/layout";
import classNames from "classnames";

import { AccessibilityProps } from "../../AccessibilityProps";
import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { EmptyArray } from "../../utilities/EmptyArray";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { CaretAlignment, CaretType, Collapse, CollapseAppearance, CollapseVariantComponentType, VariantCollapseProps } from "../Collapse";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/** A expanded IDs change handler function. */
export interface ExpandedIdsChangeHandler {
  /**
   * Raises that the set of expanded collapse IDs has changed.
   * @param expandedIds The IDs of the collapses that are expanded.
   */
  (expandedIds: ReadonlyArray<string>): void;
}

/** The `CollapseGroup` component properties. */
export interface CollapseGroupProps extends BaseProps, AccessibilityProps {
  /** The set of expanded IDs. */
  readonly expandedIds?: ReadonlyArray<string>;

  /** Called when the expanded IDs change. */
  readonly onExpandedIdsChange?: ExpandedIdsChangeHandler;

  /** The spacing between items. If not specified defaults to "medium". */
  readonly spacing?: Spacing;

  /** The collapse groups. */
  readonly children?: ReactNode;

  /**
   * Specifies the appearance of the collapse group. If not specified defaults to `'contained'`.
   * @deprecated Make use of the `collapseComponent`, passing in an appropriate collapse variant and `caret` properties instead.
   */
  readonly appearance?: CollapseAppearance;

  /** The caret type. If not specified defaults to `icon`. Only applies if `collapseComponent` is set. */
  readonly caret?: CaretType;

  /**
   * Indicates which side to align the collapse carets. If not specified defaults to `'right'`.
   * NOTE: Only applies if `appearance` is not `"checkbox"`.
   */
  readonly alignCaret?: CaretAlignment;

  /** Indicates whether only a single collapse can be open at a time. */
  readonly exclusive?: boolean;

  /**
   * A wrapper container that goes around the individual collapse components.
   * @deprecated Make use of the `collapseComponent` property instead.
   */
  readonly itemContainer?: ComponentType;

  /** The component to use for rendering collapse items. If not specified defaults to `Collapse.Contained`. */
  readonly collapseComponent?: CollapseVariantComponentType<VariantCollapseProps>;
}

const useResolvedCollapseGroupStyles = createComponentStyleHook<CollapseGroupProps>({
  legacyStyles,
  tokenStyles,
});

/**
 * Gets the tabs from the specified children.
 * @param children The children to get the tabs from.
 * @returns The array of tabs.
 */
function getCollapses(children: ReactNode | undefined): ReadonlyArray<CollapseGroupCollapseProps> {
  const result: CollapseGroupCollapseProps[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === CollapseGroup.Collapse) {
      result.push(child.props);
    } else if (child.type === Fragment) {
      result.push(...getCollapses(child.props.children));
    } else {
      if (process.env.NODE_ENV !== "production") {
        console.warn("CollapseGroup: ignoring unsupported child component.");
      }
    }
  });

  return result.length ? result : EmptyArray;
}

/** Groups a set of `Collapse` components together and manages their collapsed/expanded state. */
export const CollapseGroup = (props: CollapseGroupProps) => {
  const {
    expandedIds = EmptyArray as ReadonlyArray<string>,
    spacing,
    appearance,
    alignCaret,
    children,
    exclusive = false,
    onExpandedIdsChange,
    "aria-label": ariaLabel,
  } = props;

  const { mode, StyleModeBoundary } = useResolvedCollapseGroupStyles(props);

  let { caret = "icon", collapseComponent: CollapseComponent, itemContainer: ItemContainer = Fragment } = props;

  // #region DEPRECATED: Backwards compatibility with `appearance` until removal.

  if (!CollapseComponent) {
    // If we don't have a collapse component set then our caret is always icon for each variant except for checkbox further down.
    caret = "icon";

    switch (appearance) {
      case "blank":
        CollapseComponent = Collapse.Blank;
        break;
      case "blank-no-indent":
        CollapseComponent = Collapse.Blank.Unpadded;
        break;
      case "underlined":
        CollapseComponent = Collapse.Underlined;
        break;
      case "underlined-no-indent":
        CollapseComponent = Collapse.Underlined.Unpadded;
        break;
      case "checkbox":
        caret = "checkbox";
        CollapseComponent = Collapse.Contained;
        break;
      case "contained":
      default:
        CollapseComponent = Collapse.Contained;
        break;
    }
  } else {
    // If we have a collapse component set, always set `ItemContainer` to fragment so it renders as nothing.
    ItemContainer = Fragment;
  }

  const GroupContainer = CollapseComponent.CollapseGroupContainer ?? Stack;

  // #endregion

  const expandedIdsSet = useMemo<ReadonlySet<string>>(() => new Set(expandedIds), [expandedIds]);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: "collapse-group",
    testIdPrefix: "collapse-group",
  });

  const collapses = useMemo(() => getCollapses(children), [children]);

  const toggleExpandHandler = useCallback(
    (id: string, expanded: boolean) => {
      if (!expanded) {
        const result = exclusive ? [] : Array.from(expandedIdsSet).filter((expandedId) => expandedId !== id);
        onExpandedIdsChange?.(result);
      } else {
        if (!expandedIdsSet.has(id)) {
          const result = exclusive ? [id] : [...expandedIdsSet.values(), id];
          onExpandedIdsChange?.(result);
        }
      }
    },
    [expandedIdsSet, onExpandedIdsChange, exclusive],
  );

  return (
    <StyleModeBoundary mode={mode}>
      <div {...baseHtmlProps}>
        <GroupContainer spacing={spacing} role="group" aria-label={ariaLabel}>
          {collapses.map((collapse) => {
            return (
              <ItemContainer key={collapse.id}>
                <div
                  className={classNames(
                    "collapse-group-item",
                    { "collapse-group-item-no-spacing": spacing === "none" },
                    collapse.className,
                  )}
                  style={collapse.style}
                >
                  <CollapseComponent
                    testId={collapse.testId}
                    title={collapse.title}
                    caret={caret}
                    alignCaret={alignCaret}
                    keepHiddenContentInDOM={collapse.keepHiddenContentInDOM}
                    expanded={expandedIdsSet.has(collapse.id)}
                    onToggleExpand={(expanded) => toggleExpandHandler(collapse.id, expanded)}
                  >
                    {collapse.children}
                  </CollapseComponent>
                </div>
              </ItemContainer>
            );
          })}
        </GroupContainer>
      </div>
    </StyleModeBoundary>
  );
};

/** The `CollapseGroup.Collapse` component properties. */
export interface CollapseGroupCollapseProps extends BaseProps {
  /** The collapse ID. */
  readonly id: string;

  /** The collapse title. */
  readonly title?: ReactNode;

  /** Indicates whether a closed collapse keeps content in the DOM. If not specified defaults to `false`. */
  readonly keepHiddenContentInDOM?: boolean;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Represents a single collapse which can be placed within the `CollapseGroup` component. */
CollapseGroup.Collapse = (_: CollapseGroupCollapseProps): ReactNode => {
  return null;
};
